const STORAGE_VERSION = 1;
const DEFAULT_PREFIX = 'serpent.save.v1';
const SLOT_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function assertStorage(storage) {
  for (const method of ['getItem', 'setItem', 'removeItem']) {
    if (!storage || typeof storage[method] !== 'function') {
      throw new TypeError(`storage must provide ${method}()`);
    }
  }
  return storage;
}

export function normalizeSaveSlotId(value) {
  if (typeof value !== 'string') throw new TypeError('slotId must be a string');
  const normalized = value.trim().toLowerCase();
  if (!SLOT_PATTERN.test(normalized)) {
    throw new RangeError('slotId must contain 1-64 lowercase letters, numbers, dots, dashes, or underscores');
  }
  return normalized;
}

function normalizeIndex(value) {
  if (!value) return { version: STORAGE_VERSION, slots: [] };
  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new RangeError('Save-slot index is corrupted');
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new RangeError('Save-slot index is invalid');
  }
  if (parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.slots)) {
    throw new RangeError('Save-slot index version is unsupported');
  }
  const ids = new Set();
  const slots = parsed.slots.map((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      throw new RangeError('Save-slot index contains an invalid entry');
    }
    const slotId = normalizeSaveSlotId(entry.slotId);
    if (ids.has(slotId)) throw new RangeError('Save-slot index contains duplicate slots');
    ids.add(slotId);
    return clone({ ...entry, slotId });
  });
  return { version: STORAGE_VERSION, slots };
}

function summaryFor(slotId, envelope) {
  return {
    slotId,
    version: envelope.version,
    title: typeof envelope.metadata?.title === 'string'
      ? envelope.metadata.title
      : slotId,
    createdAt: envelope.createdAt,
    updatedAt: envelope.updatedAt,
    checksum: envelope.checksum,
    mode: envelope.metadata?.mode ?? null,
    activeMissionId: envelope.metadata?.activeMissionId ?? null,
    completedMissionCount: envelope.metadata?.completedMissionCount ?? 0,
    tick: envelope.session?.simulation?.nextTick ?? 0,
    simulationTime: envelope.session?.clock?.simulationTime ?? 0,
  };
}

export class BrowserSaveStorage {
  constructor({ storage = globalThis.localStorage, prefix = DEFAULT_PREFIX } = {}) {
    this.storage = assertStorage(storage);
    if (typeof prefix !== 'string' || prefix.trim().length === 0) {
      throw new TypeError('prefix must be a non-empty string');
    }
    this.prefix = prefix.trim();
    this.indexKey = `${this.prefix}.index`;
  }

  #slotKey(slotId) {
    return `${this.prefix}.slot.${normalizeSaveSlotId(slotId)}`;
  }

  #readIndex() {
    return normalizeIndex(this.storage.getItem(this.indexKey));
  }

  #writeIndex(index) {
    this.storage.setItem(this.indexKey, JSON.stringify(index));
  }

  list() {
    const index = this.#readIndex();
    return deepFreeze(index.slots
      .map((entry) => clone(entry))
      .sort((left, right) => (
        String(right.updatedAt).localeCompare(String(left.updatedAt))
        || left.slotId.localeCompare(right.slotId)
      )));
  }

  read(slotId) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    const raw = this.storage.getItem(this.#slotKey(normalizedSlotId));
    if (raw === null) return null;
    try {
      return JSON.parse(raw);
    } catch {
      throw new RangeError(`Save slot ${normalizedSlotId} is corrupted`);
    }
  }

  write(slotId, envelope) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)) {
      throw new TypeError('envelope must be an object');
    }
    const slotKey = this.#slotKey(normalizedSlotId);
    const previousSlot = this.storage.getItem(slotKey);
    const previousIndex = this.storage.getItem(this.indexKey);
    const index = this.#readIndex();
    const summary = summaryFor(normalizedSlotId, envelope);
    const nextSlots = index.slots.filter((entry) => entry.slotId !== normalizedSlotId);
    nextSlots.push(summary);

    try {
      this.storage.setItem(slotKey, JSON.stringify(envelope));
      this.#writeIndex({ version: STORAGE_VERSION, slots: nextSlots });
    } catch (error) {
      if (previousSlot === null) this.storage.removeItem(slotKey);
      else this.storage.setItem(slotKey, previousSlot);
      if (previousIndex === null) this.storage.removeItem(this.indexKey);
      else this.storage.setItem(this.indexKey, previousIndex);
      throw error;
    }
    return deepFreeze(clone(summary));
  }

  delete(slotId) {
    const normalizedSlotId = normalizeSaveSlotId(slotId);
    const slotKey = this.#slotKey(normalizedSlotId);
    const previousSlot = this.storage.getItem(slotKey);
    if (previousSlot === null) return false;
    const previousIndex = this.storage.getItem(this.indexKey);
    const index = this.#readIndex();
    try {
      this.storage.removeItem(slotKey);
      this.#writeIndex({
        version: STORAGE_VERSION,
        slots: index.slots.filter((entry) => entry.slotId !== normalizedSlotId),
      });
    } catch (error) {
      this.storage.setItem(slotKey, previousSlot);
      if (previousIndex === null) this.storage.removeItem(this.indexKey);
      else this.storage.setItem(this.indexKey, previousIndex);
      throw error;
    }
    return true;
  }

  clear() {
    const index = this.#readIndex();
    for (const entry of index.slots) this.storage.removeItem(this.#slotKey(entry.slotId));
    this.storage.removeItem(this.indexKey);
    return this.getStatus();
  }

  getStatus() {
    const slots = this.list();
    return deepFreeze({
      version: STORAGE_VERSION,
      prefix: this.prefix,
      slotCount: slots.length,
      slotIds: slots.map((entry) => entry.slotId).sort(),
    });
  }
}
